// Focusable elements a dialog can move focus to.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  'summary',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// Registers a listener that is removed when the signal aborts. The removal is done by
// hand as well: iOS 14.5 ignores the `signal` option of addEventListener.
export function listen(target, type, handler, signal, options = {}) {
  target.addEventListener(type, handler, { ...options, signal });
  signal.addEventListener('abort', () => target.removeEventListener(type, handler, options), { once: true });
}

export function focusableIn(root) {
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.closest('[inert]') && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden'
  );
}

/*
  Modal focus management for any dialog element:
  - on activate, focus goes to the dialog title (its aria-labelledby target) or the first control;
  - Tab and Shift+Tab cycle inside the dialog (plus any `extra` elements, e.g. an outside toggle);
  - everything else on the page gets `inert` (kept elements: the dialog, `extra`, and
    [data-a11y-keep], e.g. the live status region); the JS trap stays the main guard because
    iOS 14.5 has no `inert`;
  - on deactivate, focus returns to the element that was focused when the dialog opened.
*/
export class DialogFocus {
  constructor(dialog, { extra = [] } = {}) {
    this.dialog = dialog;
    this.extra = extra.filter(Boolean);
    this.inerted = [];
    this.controller = null;
  }

  get active() {
    return this.controller !== null;
  }

  activate(opener) {
    if (this.active) return;
    this.opener = opener || document.activeElement;
    this.controller = new AbortController();
    const { signal } = this.controller;
    listen(document, 'keydown', (event) => this.onKeydown(event), signal);
    listen(document, 'focusin', (event) => this.onFocusIn(event), signal);
    this.setInert(true);
    this.focusInitial();
    // A drawer that is still sliding in (visibility transition) can refuse focus: try again once it is shown.
    if (!this.dialog.contains(document.activeElement)) {
      setTimeout(() => {
        if (this.active && !this.dialog.contains(document.activeElement)) this.focusInitial();
      }, 350);
    }
  }

  deactivate({ returnFocus = true } = {}) {
    if (!this.active) return;
    this.controller.abort();
    this.controller = null;
    this.setInert(false);
    const opener = this.opener;
    this.opener = null;
    if (returnFocus && opener && document.contains(opener) && typeof opener.focus === 'function') {
      opener.focus({ preventScroll: true });
    }
  }

  contains(node) {
    return this.dialog.contains(node) || this.extra.some((el) => el.contains(node));
  }

  cycle() {
    return [...this.extra.flatMap((el) => (el.matches(FOCUSABLE_SELECTOR) ? [el] : focusableIn(el))), ...focusableIn(this.dialog)];
  }

  focusInitial() {
    const titleId = this.dialog.getAttribute('aria-labelledby');
    const title = titleId ? document.getElementById(titleId) : null;
    if (title && this.dialog.contains(title) && title.getClientRects().length > 0) {
      if (!title.hasAttribute('tabindex')) title.setAttribute('tabindex', '-1');
      title.focus({ preventScroll: true });
      return;
    }
    const first = focusableIn(this.dialog)[0];
    if (first) {
      first.focus({ preventScroll: true });
    } else {
      if (!this.dialog.hasAttribute('tabindex')) this.dialog.setAttribute('tabindex', '-1');
      this.dialog.focus({ preventScroll: true });
    }
  }

  onKeydown(event) {
    if (event.key !== 'Tab') return;
    const items = this.cycle();
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const current = document.activeElement;
    const index = items.indexOf(current);
    if (event.shiftKey && (index <= 0)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (index === -1 || current === last)) {
      event.preventDefault();
      first.focus();
    }
  }

  onFocusIn(event) {
    if (this.contains(event.target)) return;
    const first = this.cycle()[0];
    (first || this.dialog).focus({ preventScroll: true });
  }

  setInert(on) {
    if (!on) {
      this.inerted.forEach((el) => el.removeAttribute('inert'));
      this.inerted = [];
      return;
    }
    const keep = [this.dialog, ...this.extra];
    const isKept = (el) => keep.some((k) => el === k || el.contains(k)) || el.hasAttribute('data-a11y-keep');
    // Walk up from each kept element and make every sibling branch inert.
    keep.forEach((start) => {
      for (let node = start; node && node !== document.body; node = node.parentElement) {
        const parent = node.parentElement;
        if (!parent) break;
        Array.from(parent.children).forEach((sibling) => {
          if (sibling === node || isKept(sibling) || sibling.hasAttribute('inert')) return;
          if (['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT'].includes(sibling.tagName)) return;
          sibling.setAttribute('inert', '');
          this.inerted.push(sibling);
        });
      }
    });
  }
}

// Export the DrawerComponent class for other modules to extend
export class IaDrawer extends HTMLElement {
  constructor() {
    super();
    this.id = this.dataset.drawerId;
    this.isOpen = this.classList.contains('is-open');
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.window = this.querySelector('[data-drawer-window]');
    this.dialogFocus = new DialogFocus(this);
  }

  // Non-modal subclasses (dropdowns) turn this off: no focus trap, no inert.
  get isModal() {
    return true;
  }

  // In the theme editor the rest of the preview must stay clickable, so no trap and no inert there.
  get trapsFocus() {
    return this.isModal && !window.Shopify?.designMode;
  }

  connectedCallback() {
    // The storefront parks the drawer in <body>, clear of the header's stacking context. The theme
    // editor can only select a section or block that sits inside its own section, so there it stays
    // where it was rendered.
    if (this.parentNode !== document.body && !window.Shopify?.designMode) {
      document.body.appendChild(this);
      return;
    }
    this.listeners?.abort();
    this.listeners = new AbortController();
    const { signal } = this.listeners;

    if (!this.getAttribute('id')) this.setAttribute('id', `drawer-${this.id}`);

    listen(document, 'click', (event) => {
      const trigger = event.target.closest(`[data-drawer-trigger="${this.id}"]`);
      if (trigger) this.toggleDrawer(event, trigger);
    }, signal);

    listen(document, 'keydown', (event) => {
      if (this.isOpen && event.key === 'Escape') this.close();
    }, signal);

    if (this.overlay) listen(this.overlay, 'click', () => this.close(), signal);

    this.syncTriggers();
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.dialogFocus.deactivate({ returnFocus: false });
    // Removed while open (the editor re-renders the section): release the page lock it holds.
    if (this.isOpen && this.parentNode === null) {
      document.body.classList.remove('ia-overflow-hidden');
      window.iaTheme?.showExtraStickyElements();
    }
  }

  // Openers outside the dialog announce what they control and whether it is open.
  syncTriggers() {
    document.querySelectorAll(`[data-drawer-trigger="${this.id}"]`).forEach((trigger) => {
      if (this.contains(trigger)) return;
      trigger.setAttribute('aria-controls', this.getAttribute('id'));
      trigger.setAttribute('aria-expanded', String(this.isOpen));
      if (this.isModal) trigger.setAttribute('aria-haspopup', 'dialog');
    });
  }

  toggleDrawer(event, trigger) {
    event.preventDefault();
    this.isOpen ? this.close() : this.open(trigger);
  }

  close() {
    this.classList.remove('is-open');
    this.isOpen = false;
    document.body.classList.remove('ia-overflow-hidden');
    window.iaTheme.showExtraStickyElements();
    this.syncTriggers();
    if (this.trapsFocus) this.dialogFocus.deactivate();
  }

  open(opener) {
    this.classList.add('is-open');
    this.isOpen = true;
    if (document.documentElement.scrollHeight > document.documentElement.clientHeight) {
      document.body.classList.add('ia-overflow-hidden');
    }
    window.iaTheme.hideExtraStickyElements();
    this.syncTriggers();
    if (this.trapsFocus) this.dialogFocus.activate(opener instanceof Element ? opener : document.activeElement);
    this.dispatchEvent(new CustomEvent('ia-drawer-opened', {
      detail: {
        drawerId: this.id
      }
    }));
  }
}


// Only define custom element if not already defined
if (!customElements.get('ia-drawer-component')) {
  customElements.define('ia-drawer-component', IaDrawer);
}


class IaDrawerClose extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.close.bind(this));
    // A custom element is not a control by itself: give it button semantics and keys.
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.close();
      }
    });
  }

  connectedCallback() {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
  }


  close() {
    const drawerId = this.dataset.drawerId;
    document.querySelector(`[data-drawer-id="${drawerId}"]`)?.close();
  }
}

if (!customElements.get('ia-drawer-close')) {
  customElements.define('ia-drawer-close', IaDrawerClose);
}


// Export DrawerMenu class for completeness
export class IaDrawerMenu extends IaDrawer {
  constructor() {
    super();
    this.submenuOpener = this.querySelectorAll('[data-sub-menu-opener]');
    this.parentMenu = this.querySelector('[data-parent-menu]');
    this.isSubmenuOpen = false;
    this.submenuOpener?.forEach(submenuOpener => {
      submenuOpener.addEventListener('click', this.toggleSubmenu.bind(this));
    });
  }

  toggleSubmenu(event) {
    event.preventDefault();
    this.isSubmenuOpen = !this.isSubmenuOpen;
    const submenuId = event.currentTarget.dataset.subMenuOpener;
    const submenu = this.querySelector(`[data-sub-menu="${submenuId}"]`);
    submenu?.classList.toggle('is-open', this.isSubmenuOpen);
    if (submenu && submenu.classList.contains('is-open')) {
      this.parentMenu?.classList.add('ia-hidden');
    } else {
      this.parentMenu?.classList.remove('ia-hidden');
    }
    this.querySelectorAll(`[data-sub-menu]`)?.forEach(anotherSubmenu => {
      if (anotherSubmenu.dataset.subMenu !== submenuId) {
        anotherSubmenu.classList.remove('is-open');
      }
    });
  }

  close() {
    super.close();
    this.isSubmenuOpen = false;
    this.querySelectorAll(`[data-sub-menu]`)?.forEach(anotherSubmenu => {
      anotherSubmenu.classList.remove('is-open');
    });
    this.parentMenu?.classList.remove('ia-hidden');
  }
}

// Only define custom element if not already defined
if (!customElements.get('ia-menu-drawer')) {
  customElements.define('ia-menu-drawer', IaDrawerMenu);
}

class IaDropdown extends IaDrawer {
  get isModal() {
    return false;
  }
}

if (!customElements.get('ia-dropdown')) {
  customElements.define('ia-dropdown', IaDropdown);
}
